import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { searchGoals } from '../../api/goals';
import { Goal } from '../../api/types/Goal';
import { VentureId } from '../../api/ventures/types/Venture';
import GoalsTableBasic from '../../components/tables/goals-table-basic';
import { VentureContext } from '../../contexts/venture-context';

function VentureGoalsPage() {
  const { ventureId } = useParams<{ ventureId: VentureId }>();
  const { venture } = useContext(VentureContext);

  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isNextPageLoading, setIsNextPageLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(1);

  const loadGoals = useCallback(
    async (pageNum: number) => {
      setIsLoading(true);
      setGoals([]);
      const currentPage = pageNum - 1;
      const loaded = await searchGoals({
        ventureId,
        pageNum: currentPage,
      });
      setGoals(loaded);
      setIsLoading(false);

      if (loaded.length === 10) {
        setIsNextPageLoading(true);
        try {
          const nextLoaded = await searchGoals({
            ventureId,
            pageNum: currentPage + 1,
          });
          setHasNextPage(nextLoaded.length > 0);
        } catch {
          setHasNextPage(false);
        } finally {
          setIsNextPageLoading(false);
        }
      } else {
        setHasNextPage(false);
      }
    },
    [ventureId],
  );

  useEffect(() => {
    if (!ventureId) return;
    loadGoals(1);
  }, [ventureId, loadGoals]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setPage(nextPage);
      loadGoals(nextPage);
    },
    [loadGoals],
  );

  const handleGoalSliderChange = useCallback(
    ({ id, progress, status }) =>
      setGoals((prev) =>
        prev.map((g) => (g.id === id ? { ...g, progress, status } : g)),
      ),
    [],
  );

  return (
    <GoalsTableBasic
      goals={goals}
      page={page}
      hasNextPage={hasNextPage}
      isNextPageLoading={isNextPageLoading}
      isLoading={isLoading}
      venture={
        venture
          ? { id: venture.id, ventureName: venture.ventureName }
          : undefined
      }
      onPageChange={handlePageChange}
      onGoalSliderChange={handleGoalSliderChange}
      hideTopBorder
    />
  );
}

export default VentureGoalsPage;
